import { IconButton, Menu, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import EmojiPicker, { EmojiClickData } from '@/common/components/lazy-emoji-picker/lazy-emoji-picker';
import React, { useRef, useState } from 'react';
import { IoMdCloseCircle } from 'react-icons/io';
import { MdInsertEmoticon } from 'react-icons/md';

type Props = {
    onEmojiSelect: (emoji: string) => void;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
};

export default function ReusableEmojiPickerButton({ onEmojiSelect, disabled, size = 'small' }: Props) {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const toggle = () => {
        if (disabled) return;
        setIsOpen(prev => !prev);
    };

    const handleEmojiClick = (emoji: EmojiClickData) => {
        onEmojiSelect(emoji.emoji);
        setIsOpen(false);
    };

    return (
        <>
            <Tooltip
                title={isOpen ? 'Close emoji picker' : 'Open emoji picker'}
                arrow
                placement="top"
            >
                <span>
                    <IconButton
                        ref={buttonRef}
                        onClick={toggle}
                        disabled={disabled}
                        focusRipple
                        aria-label={isOpen ? 'Close emoji picker' : 'Open emoji picker'}
                        size={size}
                        sx={{
                            transition: 'all 0.2s ease-in-out',
                            borderRadius: '6px',
                            '&:hover': { transform: 'scale(1.05)' },
                            '&:active': { transform: 'scale(0.95)' },
                        }}
                    >
                        {isOpen
                            ? <IoMdCloseCircle style={{ color: 'var(--color-primary)' }} />
                            : <MdInsertEmoticon style={{ color: 'var(--color-primary)' }} />}
                    </IconButton>
                </span>
            </Tooltip>
            <Menu
                open={isOpen}
                anchorEl={buttonRef.current}
                onClose={() => setIsOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { padding: 0 } } }}
            >
                <Box sx={{ pointerEvents: 'auto' }}>
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                </Box>
            </Menu>
        </>
    );
}
